import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'

const BookedRoomCard = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(20)};
`

const ImageColumn = styled.div`
  width: ${rem(237)};
  height: ${rem(137)};
  flex-shrink: 0;
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;
`

const DateBox = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(16)};
`

interface BookedRoomCardItemProps {
  room: DrawerHotelBookedRoom
  index: number
  totalRooms: number
}

function BookedRoomCardItem({ room, index, totalRooms }: BookedRoomCardItemProps) {
  const intl = useIntl()

  const checkInLabel = intlDateFormatter(room.checkIn, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)
  const checkOutLabel = intlDateFormatter(room.checkOut, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)

  const carouselImages = useMemo<Array<App.Image>>(
    () => room.roomImageIds.map((id) => ({ id })),
    [room.roomImageIds],
  )
  const singleImageId = carouselImages.length === 1 ? carouselImages[0].id : undefined

  let roomImage: ReactNode = null
  if (singleImageId) {
    roomImage = <ResponsiveImage id={singleImageId} alt={room.roomTypeName} fit="center" />
  } else if (carouselImages.length > 1) {
    roomImage = <ImageCarousel images={carouselImages} arrowBehaviour="interaction-visible" />
  }

  // Mirrors HotelRoomSummary.tsx:166-180 — single room shows "Your Room",
  // multi-room shows "Room {n}" (1-indexed) so each card is identifiable.
  const roomLabel = totalRooms === 1
    ? intl.formatMessage({
        id: 'mxARc7OBIezIoQHOptV5',
        defaultMessage: 'Your Room',
        description: 'Label for the single room in hotel booking summary',
      })
    : intl.formatMessage(
        {
          id: 'Oqb9kyhd11EEaEGE9nQe',
          defaultMessage: 'Room {roomNumber}',
          description: 'Label for a numbered room in hotel booking summary with multiple rooms',
        },
        { roomNumber: index + 1 },
      )

  const bookingDetailsUrl = `/account/my-escapes/order/${room.orderId}`

  const checkInHeader = intl.formatMessage({
    id: 'FRU2pokXnES2bxnLJkdy',
    defaultMessage: 'Check-in',
    description: 'Label above the check-in date in a booked room card',
  })
  const checkOutHeader = intl.formatMessage({
    id: 'fJO0_3AVTgQCU7_gg4gU',
    defaultMessage: 'Check-out',
    description: 'Label above the check-out date in a booked room card',
  })

  return (
    <BookedRoomCard>
      <Group direction="vertical" gap={16}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <StructureLabel variant="standard">{roomLabel}</StructureLabel>
          {room.bookingNumber && (
            <BodyTextBlock variant="medium">
              {intl.formatMessage(
                {
                  id: '96MtMoe3WeX_KXHYg5XT',
                  defaultMessage: '<b>Booking ID:</b> {bookingNumber}',
                  description: 'Label for hotel booking ID number on the hotel or room booking page',
                },
                {
                  b: (chunks: ReactNode) => <b>{chunks}</b>,
                  bookingNumber: room.bookingNumber,
                },
              )}
            </BodyTextBlock>
          )}
        </Group>

        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6">{room.roomTypeName}</Heading>
              {room.packageName && (
                <BodyText variant="medium" colour="neutral-two">
                  {room.packageName}
                </BodyText>
              )}
            </Group>
            {roomImage && (
              <ImageColumn>
                {roomImage}
              </ImageColumn>
            )}
          </Group>

          {(checkInLabel || checkOutLabel) && (
            <DateBox>
              <Group direction="horizontal" gap={12} verticalAlign="center">
                <LineCalendarIcon size="L" colour="neutral-one" />
                <Group direction="vertical">
                  <BodyText variant="small" colour="neutral-two">{checkInHeader}</BodyText>
                  <BodyText variant="medium" weight="bold">{checkInLabel}</BodyText>
                </Group>
                <LineArrowRightIcon colour="neutral-three" />
                <Group direction="vertical">
                  <BodyText variant="small" colour="neutral-two">{checkOutHeader}</BodyText>
                  <BodyText variant="medium" weight="bold">{checkOutLabel}</BodyText>
                </Group>
              </Group>
            </DateBox>
          )}

          <TextLink size="medium" href={bookingDetailsUrl} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage({
              id: 'pKtkMInfl7gquCBZNrVf',
              defaultMessage: 'View all details',
              description: 'Link text to view full booking details on the my escapes page',
            })}
          </TextLink>
        </Group>
      </Group>
    </BookedRoomCard>
  )
}

interface Props {
  bookedRooms: Array<DrawerHotelBookedRoom>
}

function HotelDetailBookedRoom({ bookedRooms }: Props) {
  const intl = useIntl()

  return (
    <Group direction="vertical" gap={12}>
      <Heading variant="heading5">
        {intl.formatMessage({
          id: 'jiBFd9qhjviP_yYV2tN3',
          defaultMessage: 'Room details',
          description: 'Heading for the booked room section in the hotel detail drawer',
        })}
      </Heading>
      {bookedRooms.map((room, index) => (
        <BookedRoomCardItem
          key={room.bookingNumber ?? `${room.orderId}-${index}`}
          room={room}
          index={index}
          totalRooms={bookedRooms.length}
        />
      ))}
    </Group>
  )
}

export default HotelDetailBookedRoom
